import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { X, User, Calendar, Calculator, ExternalLink, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatPhone } from '../../utils/phoneUtils';
import { useCompany } from '../../context/CompanyContext';
import { db } from '../../firebase';
import { doc, updateDoc, collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import QuickQuoteModal from './QuickQuoteModal';
import AttachLeadModal from './AttachLeadModal';

/**
 * Safely parse a Firestore timestamp or date value.
 * Returns null if the value is invalid or missing.
 */
const safeParseDate = (value) => {
  if (!value) return null;
  try {
    // Firestore Timestamp object
    if (value.toDate && typeof value.toDate === 'function') {
      return value.toDate();
    }
    // Already a Date object
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    // ISO string or timestamp number
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

/**
 * Safely format a date, returning a fallback if invalid.
 */
const safeFormatDate = (value, formatStr, fallback = 'Unknown') => {
  const date = safeParseDate(value);
  if (!date) return fallback;
  try {
    return format(date, formatStr);
  } catch {
    return fallback;
  }
};


const ConversationDetails = ({ conversation, onClose }) => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [localTags, setLocalTags] = useState(conversation?.tags || []);
  const [teamMembers, setTeamMembers] = useState([]);
  const [attachLeadOpen, setAttachLeadOpen] = useState(false);
  const [quickQuoteOpen, setQuickQuoteOpen] = useState(false);
  const [linkQuoteToLead, setLinkQuoteToLead] = useState(true);

  const [pendingQuote, setPendingQuote] = useState(null);

  useEffect(() => {
    const loadTeam = async () => {
      if (!companyId || !db) return;
      try {
        const membershipsSnap = await getDocs(collection(db, 'companies', companyId, 'users'));
        const members = membershipsSnap.docs.map((m) => ({ id: m.id, ...(m.data() || {}) }));
        setTeamMembers(members);
      } catch (e) {
        console.error('Failed to load team members for assignment', e);
      }
    };
    loadTeam();
  }, [companyId]);

  if (!conversation) {
    return null;
  }

  const createdDate = safeParseDate(conversation.createdAt);
  const lastMessageDate = safeParseDate(conversation.lastMessageTime);

  const handleSaveName = async () => {
    if (!companyId || !db || !conversation?.id) return;
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    try {
      const ref = doc(db, 'companies', companyId, 'conversations', conversation.id);
      await updateDoc(ref, { displayName: trimmed });
      setEditingName(false);
    } catch (e) {
      console.error('Failed to save displayName', e);
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!companyId || !db || !conversation?.id) return;
    const trimmed = tagsInput.trim();
    if (!trimmed) return;
    if (localTags.includes(trimmed)) {
      setTagsInput('');
      return;
    }
    const next = [...localTags, trimmed];
    setLocalTags(next);
    setTagsInput('');
    try {
      const ref = doc(db, 'companies', companyId, 'conversations', conversation.id);
      await updateDoc(ref, { tags: next });
    } catch (e) {
      console.error('Failed to add tag', e);
    }
  };

  const handleRemoveTag = async (tag) => {
    if (!companyId || !db || !conversation?.id) return;
    const next = (localTags || []).filter((t) => t !== tag);
    setLocalTags(next);
    try {
      const ref = doc(db, 'companies', companyId, 'conversations', conversation.id);
      await updateDoc(ref, { tags: next });
    } catch (e) {
      console.error('Failed to remove tag', e);
    }
  };

  const handleAssign = async (userId) => {
    if (!companyId || !db || !conversation?.id) return;
    try {
      const ref = doc(db, 'companies', companyId, 'conversations', conversation.id);
      await updateDoc(ref, { assignedToUserId: userId || null });
    } catch (e) {
      console.error('Failed to assign conversation', e);
    }
  };



  const handleCreateLead = () => {
    // For now, route to Leads page; operators can create a lead from there.
    navigate('/leads');
  };

  return (
    <>
      <div className="h-full bg-white border-l border-gray-200 overflow-y-auto">
        {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
        <h3 className="text-lg font-semibold text-gray-900">Details</h3>
        <button
          onClick={onClose}
          className="lg:hidden p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Contact Info Card */}
        <div className="bg-white shadow-sm rounded-xl p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
            <User className="h-4 w-4" />
            Contact Information
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-gray-900 font-semibold text-sm">
                {conversation.displayName || conversation.clientName || "Unknown Contact"}
              </p>
              {!conversation.displayName && !conversation.clientName && (
                <button
                  type="button"
                  className="mt-1 text-[11px] text-indigo-600 hover:underline"
                  onClick={() => setEditingName(true)}
                >
                  + Add name
                </button>
              )}
              {editingName && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Contact name"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#0A2647]"
                  />
                  <button
                    type="button"
                    onClick={handleSaveName}
                    className="px-2 py-1 text-xs rounded bg-[#0A2647] text-white hover:bg-[#0A2647]/90"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingName(false)}
                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
              <p className="text-gray-600 text-xs mt-1">
                {formatPhone(conversation.clientPhone) || conversation.clientPhone}
              </p>
              {conversation.email && (
                <p className="text-gray-600 text-xs mt-1">{conversation.email}</p>
              )}
              {conversation.pipelineStage && (
                <p className="inline-block text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 mt-2">
                  {conversation.pipelineStage}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Timeline Card */}
        <div className="bg-white shadow-sm rounded-xl p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Timeline
          </h3>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-gray-500">First Contact</p>
              <p className="text-sm text-gray-900">
                {safeFormatDate(conversation.createdAt, 'MMM d, yyyy h:mm a', 'Recently')}
              </p>
            </div>
            {lastMessageDate && (
              <div>
                <p className="text-xs text-gray-500">Last Message</p>
                <p className="text-sm text-gray-900">
                  {safeFormatDate(conversation.lastMessageTime, 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tags Card */}
        <div className="bg-white shadow-sm rounded-xl p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Tags
          </h3>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1">
              {localTags && localTags.length > 0 ? (
                localTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    {tag}
                    <span className="ml-1 text-gray-400">×</span>
                  </button>
                ))
              ) : (
                <p className="text-xs text-gray-500 italic">No tags yet</p>
              )}
            </div>
            <form onSubmit={handleAddTag} className="flex items-center gap-2 mt-1">
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="Add tag and press Enter"
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#0A2647]"
              />
            </form>
          </div>
        </div>

        {/* Assignment Card */}
        <div className="bg-white shadow-sm rounded-xl p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Assignment</h3>
          <div className="text-sm text-gray-600 space-y-2">
            <p className="text-xs text-gray-500">Assigned to</p>
            <select
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#0A2647] bg-white"
              value={conversation.assignedToUserId || conversation.assignedTo || ''}
              onChange={(e) => handleAssign(e.target.value)}
            >
              <option value="">Unassigned</option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.displayName || member.email || member.id}
                </option>
              ))}
            </select>

      <QuickQuoteModal
        open={quickQuoteOpen}
        onClose={() => setQuickQuoteOpen(false)}
        conversation={conversation}
        companyId={companyId}
        onQuoteCreated={(info) => {
          setPendingQuote(info);
          setQuickQuoteOpen(false);
          if (info && conversation?.id) {
            const event = new CustomEvent('inbox:quote-created', {
              detail: {
                ...info,
                conversationId: conversation.id,
              },
            });
            window.dispatchEvent(event);
          }
        }}
      />

          </div>
        </div>

        {/* Quick Actions Card */}
        <div className="bg-white shadow-sm rounded-xl p-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Quick Actions</h3>
          <div className="space-y-2">
            <button
              onClick={() => setQuickQuoteOpen(true)}
              className="w-full px-4 py-2 bg-[#0A2647] text-white rounded-lg hover:bg-[#0A2647]/90 transition-colors flex items-center gap-2 justify-center text-sm"
            >
              <Calculator className="h-4 w-4" />
              Create Quote
            </button>
            <button
              onClick={handleCreateLead}
              className="w-full px-4 py-2 rounded-lg transition-colors flex items-center gap-2 justify-center text-sm bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <ExternalLink className="h-4 w-4" />
              Create Lead
            </button>
            <button
              onClick={() => setAttachLeadOpen(true)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2 justify-center text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Attach Existing Lead
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <AttachLeadModal
      open={attachLeadOpen}
      onClose={() => setAttachLeadOpen(false)}
      hasQuote={!!conversation.latestQuoteId}
      defaultLinkQuote
      onAttach={({ leadId, linkQuote }) => {
        // TODO: Implement linking latest quote to this lead via backend/Firestore
        setAttachLeadOpen(false);
      }}
    />
    </>
  );
};

export default ConversationDetails;
